import { defineStore } from "pinia"
import { ref, computed } from "vue"
import axios from "axios"

export interface AuthUser {
  id: number
  email_address: string
  name: string | null
  timezone: string | null
  onboarding_completed_at: string | null
}

interface LoginResponse {
  access_token: string
  refresh_token: string
  user: AuthUser
}

const STORAGE_KEY = "focusflow.auth"

function load(): { access: string | null; refresh: string | null; user: AuthUser | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : { access: null, refresh: null, user: null }
  } catch {
    return { access: null, refresh: null, user: null }
  }
}

function persist(state: { access: string | null; refresh: string | null; user: AuthUser | null }) {
  try {
    if (state.access) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    /* private mode — silently degrade */
  }
}

export const useAuthStore = defineStore("auth", () => {
  const initial = load()
  const accessToken = ref<string | null>(initial.access)
  const refresh = ref<string | null>(initial.refresh)
  const user = ref<AuthUser | null>(initial.user)
  const isAuthenticated = computed(() => Boolean(accessToken.value && user.value))

  function setSession(payload: LoginResponse) {
    accessToken.value = payload.access_token
    refresh.value = payload.refresh_token
    user.value = payload.user
    persist({ access: accessToken.value, refresh: refresh.value, user: user.value })
  }

  function clear() {
    accessToken.value = null
    refresh.value = null
    user.value = null
    persist({ access: null, refresh: null, user: null })
  }

  return { accessToken, refresh, user, isAuthenticated, setSession, clear }
})

/* Module-level helpers so the axios interceptor can read auth state without
 * pulling Vue into request paths. */

export function getAccessToken(): string | null {
  try {
    return useAuthStore().accessToken
  } catch {
    return load().access
  }
}

export function clearAuth(): void {
  try {
    useAuthStore().clear()
  } catch {
    persist({ access: null, refresh: null, user: null })
  }
}

export async function refreshToken(): Promise<string | null> {
  let store
  try { store = useAuthStore() } catch { return null }
  if (!store.refresh) return null
  try {
    const { data } = await axios.post<LoginResponse>("/api/v1/auth/refresh", {
      refresh_token: store.refresh,
    })
    store.setSession(data)
    return data.access_token
  } catch {
    store.clear()
    return null
  }
}

export function hydrateAuth(): void {
  /* No-op: store hydrates from localStorage in its setup function.
   * Exists so application.ts has an explicit anchor for the boot sequence. */
}
